import { fileEtag, directoryEtag } from './etag.js'

/**
 * Percent-encode the path portion of a D:href, leaving any absolute-URI
 * prefix intact.
 *
 * RFC 4918: D:href is a URI, so each path segment is percent-encoded (nephele's
 * getCanonicalUrl does the same). Raw spaces here made WebDavSync see
 * "WhatsApp Video x.mp4" on this side and "WhatsApp%20Video%20x.mp4" from
 * nephele — never equal, so the same files re-copied on every sync.
 *
 * The href may now arrive absolute (nephele emits
 * `https://host/path/`). Splitting the whole string on '/' and encoding every
 * piece would turn `https:` into `https%3A` and `//` into empty segments, so
 * the scheme+authority is held aside and only the path is encoded.
 *
 * @param {string} href — relative ('/a/b') or absolute ('https://h/a/b')
 * @returns {string}
 */
export function encodeHrefPath(href) {
    const match = /^([a-z][a-z0-9+.-]*:\/\/[^/]*)(.*)$/i.exec(href)
    const origin = match ? match[1] : ''
    const path = match ? match[2] : href
    return origin + path.split('/').map(encodeURIComponent).join('/')
}

export class XMLBuilder {
    escapeXML(str) {
        if (typeof str !== 'string') return str
        return str
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;')
    }

    buildMultistatusXML(responses) {
        return `<?xml version="1.0" encoding="utf-8"?>
<D:multistatus xmlns:D="DAV:">
${responses.join('')}
</D:multistatus>`
    }

    buildLockResponse(token, lock, resourceExists = true) {
        const status = resourceExists ? 200 : 201

        const ownerXml = lock.owner
            ? `<D:owner>${this.escapeXML(lock.owner)}</D:owner>`
            : '<D:owner/>'

        const xml = `<?xml version="1.0" encoding="utf-8"?>
<D:prop xmlns:D="DAV:">
    <D:lockdiscovery>
        <D:activelock>
            <D:locktype><D:${lock.locktype}/></D:locktype>
            <D:lockscope><D:${lock.lockscope}/></D:lockscope>
            <D:depth>${lock.depth}</D:depth>
            ${ownerXml}
            <D:timeout>${lock.timeout}</D:timeout>
            <D:locktoken>
                <D:href>${token}</D:href>
            </D:locktoken>
        </D:activelock>
    </D:lockdiscovery>
</D:prop>`

        return {
            status: status,
            headers: {
                'Content-Type': 'application/xml; charset=utf-8',
                'Lock-Token': `<${token}>`,
            },
            body: xml,
        }
    }

    getLockDiscoveryXML(path, locks) {
        const activeLocks = []
        for (const [token, lock] of locks.entries()) {
            if (
                lock.path === path ||
                (lock.depth === 'infinity' && path.startsWith(lock.path))
            ) {
                const ownerXml = lock.owner
                    ? `<D:owner>${this.escapeXML(lock.owner)}</D:owner>`
                    : '<D:owner/>'

                activeLocks.push(`
                <D:activelock>
                    <D:locktype><D:${lock.locktype}/></D:locktype>
                    <D:lockscope><D:${lock.lockscope}/></D:lockscope>
                    <D:depth>${lock.depth}</D:depth>
                    ${ownerXml}
                    <D:timeout>${lock.timeout}</D:timeout>
                    <D:locktoken>
                        <D:href>${token}</D:href>
                    </D:locktoken>
                </D:activelock>`)
            }
        }

        return activeLocks.length > 0 ? activeLocks.join('') : null
    }

    /**
     * Build a PROPFIND response element honoring the request's property
     * selection (browserdav-nephele-parity Phase 3):
     *   selection = { mode: 'allprop' | 'propname' | 'prop', props? }
     *   deadProps = { "xmlns|name": { xmlns, name, value } }
     * Unknown requested properties get a 404 propstat.
     */
    /**
     * A member the caller may not read: its href and a status, no
     * properties (RFC 4918 §9.1 lists every member; §14.24 allows a
     * response of href + status). Nephele's shape for the same refusal.
     */
    buildStatusResponse(handle, path, status, hrefPrefix = '') {
        let href = path.startsWith('/') ? path : '/' + path
        if (hrefPrefix) href = hrefPrefix + href
        if (handle?.kind === 'directory' && !href.endsWith('/')) href += '/'
        const text = { 401: 'Unauthorized', 403: 'Forbidden', 404: 'Not Found' }[status] || ''
        return `
            <D:response>
                <D:href>${this.escapeXML(encodeHrefPath(href))}</D:href>
                <D:status>HTTP/1.1 ${status} ${text}</D:status>
            </D:response>`
    }

    async buildPropResponse(handle, path, mimeTypeDetector, locks, selection = { mode: 'allprop' }, deadProps = {}, hrefPrefix = '') {
        let href = path
        if (!href.startsWith('/')) {
            href = '/' + href
        }

        // Client-visible namespace prefix (X-Forwarded-Prefix) — applied only
        // at href emission so storage paths, entry filters, and dead-property
        // keys stay proxy-agnostic (nephele resolves getCanonicalUrl against
        // Resource.baseUrl the same way).
        if (hrefPrefix) {
            href = hrefPrefix + href
        }

        if (handle.kind === 'directory' && !href.endsWith('/')) {
            href += '/'
        }

        const encodedHref = this.escapeXML(encodeHrefPath(href))

        // Live properties as complete elements, keyed by DAV: local name.
        // creationdate deliberately omitted: the File System Access API has
        // no birthtime, and fabricating one at request time made every
        // response differ (substrate constraint).
        const live = {}
        if (handle.kind === 'directory') {
            live.resourcetype = '<D:resourcetype><D:collection/></D:resourcetype>'
            live.getcontenttype = '<D:getcontenttype>httpd/unix-directory</D:getcontenttype>'
            live.getcontentlength = '<D:getcontentlength>0</D:getcontentlength>'
            live.getetag = `<D:getetag>"${await directoryEtag(handle)}"</D:getetag>`
        } else {
            const file = await handle.getFile()
            const mimeType = mimeTypeDetector.detectMimeType(handle.name)
            const lastModified = file.lastModified
                ? new Date(file.lastModified).toUTCString()
                : new Date().toUTCString()
            live.resourcetype = '<D:resourcetype/>'
            live.getcontenttype = `<D:getcontenttype>${this.escapeXML(mimeType)}</D:getcontenttype>`
            live.getcontentlength = `<D:getcontentlength>${file.size}</D:getcontentlength>`
            live.getlastmodified = `<D:getlastmodified>${lastModified}</D:getlastmodified>`
            live.getetag = `<D:getetag>"${await fileEtag(file, path)}"</D:getetag>`
            live.executable = '<D:executable>F</D:executable>'
        }
        live.displayname = `<D:displayname>${this.escapeXML(handle.name)}</D:displayname>`
        const lockInfo = this.getLockDiscoveryXML(path, locks)
        if (lockInfo) {
            live.lockdiscovery = `<D:lockdiscovery>${lockInfo}</D:lockdiscovery>`
        }

        let nsCounter = 0
        const deadElement = (prop, empty) => {
            const prefix = `ZZ${++nsCounter}`
            return empty || prop.value === ''
                ? `<${prefix}:${prop.name} xmlns:${prefix}="${this.escapeXML(prop.xmlns)}"/>`
                : `<${prefix}:${prop.name} xmlns:${prefix}="${this.escapeXML(prop.xmlns)}">${prop.value}</${prefix}:${prop.name}>`
        }

        const found = []
        const missing = []

        if (selection.mode === 'prop') {
            for (const requested of selection.props) {
                if (requested.xmlns === 'DAV:' && live[requested.name]) {
                    found.push(live[requested.name])
                    continue
                }
                const dead = deadProps[`${requested.xmlns}|${requested.name}`]
                if (dead) {
                    found.push(deadElement(dead, false))
                } else {
                    const prefix = `ZZ${++nsCounter}`
                    missing.push(
                        requested.xmlns === 'DAV:'
                            ? `<D:${requested.name}/>`
                            : `<${prefix}:${requested.name} xmlns:${prefix}="${this.escapeXML(requested.xmlns)}"/>`
                    )
                }
            }
        } else {
            const namesOnly = selection.mode === 'propname'
            for (const [name, element] of Object.entries(live)) {
                found.push(namesOnly ? `<D:${name}/>` : element)
            }
            for (const dead of Object.values(deadProps)) {
                found.push(deadElement(dead, namesOnly))
            }
        }

        const indent = (elements) => elements.map((e) => `\n            ${e}`).join('')
        let propstats = `
            <D:propstat>
                <D:prop>${indent(found)}
                </D:prop>
                <D:status>HTTP/1.1 200 OK</D:status>
            </D:propstat>`
        if (missing.length > 0) {
            propstats += `
            <D:propstat>
                <D:prop>${indent(missing)}
                </D:prop>
                <D:status>HTTP/1.1 404 Not Found</D:status>
            </D:propstat>`
        }

        return `
        <D:response>
            <D:href>${encodedHref}</D:href>${propstats}
        </D:response>`
    }
}